import { useMemo, useCallback } from "react";
import {
  encode as coreEncode,
  decode as coreDecode,

  validate as coreValidate,
  validateLight as coreValidateLight,
  OpaqueIdException,
  OpaqueIdError,
} from "../utils";
import { useOpaqueIdStore } from "../stores/opaqueIdStore";
import { bytesToString, decodeBytes, encodeBytes, stringToBytes } from "../utils/opaqueID/opaqueId";

// Tipos para la API del hook
export interface OpaqueIdSuccessResult<T> {
  success: true;
  data: T;
}

export interface OpaqueIdHookError {
  success: false;
  error: {
    code: string;
    message: string;
    publicId?: string;
  };
}

export type OpaqueIdResponse<T> = OpaqueIdSuccessResult<T> | OpaqueIdHookError;

// Opciones para construcción de URLs
export interface BuildUrlOptions {
  basePath: string;
  prefix?: string;
  slug?: string; // Soporte para slugs SEO: /mi-slug--<id>
}

type JsonValue =
  | string
  | number
  | boolean
  | null
  | JsonValue[]
  | { [key: string]: JsonValue };

/**
 * Hook principal para manejo de identificadores opacos
 * Proporciona API completa con memoización, telemetría y manejo de errores
 */
export function useOpaqueId() {
  const config = useOpaqueIdStore((state) => state.config);
  const incrementEncodeAttempts = useOpaqueIdStore(
    (state) => state.incrementEncodeAttempts
  );
  const incrementDecodeAttempts = useOpaqueIdStore(
    (state) => state.incrementDecodeAttempts
  );
  const incrementDecodeSuccesses = useOpaqueIdStore(
    (state) => state.incrementDecodeSuccesses
  );
  const incrementDecodeFailures = useOpaqueIdStore(
    (state) => state.incrementDecodeFailures
  );

  // Memoizar funciones basadas en la config activa
  const encode = useCallback(
    (num: number): OpaqueIdResponse<string> => {
      try {
        incrementEncodeAttempts();
        const result = coreEncode(num, config);

        return {
          success: true,
          data: result,
        };
      } catch (error) {
        if (error instanceof OpaqueIdException) {
          console.warn("OpaqueId: Encode failed", {
            code: error.code,
            message: error.message,
            publicId: error.publicId,
          });

          return {
            success: false,
            error: {
              code: error.code,
              message: "Failed to generate identifier",
              publicId: error.publicId,
            },
          };
        }

        console.error("OpaqueId: Unexpected encode error", error);
        return {
          success: false,
          error: {
            code: "UNKNOWN_ERROR",
            message: "An unexpected error occurred",
          },
        };
      }
    },
    [config, incrementEncodeAttempts]
  );

  const decode = useCallback(
    (publicId: string): OpaqueIdResponse<number> => {
      try {
        incrementDecodeAttempts();
        const result = coreDecode(publicId, config);
        incrementDecodeSuccesses();

        return {
          success: true,
          data: result,
        };
      } catch (error) {
        if (error instanceof OpaqueIdException) {
          incrementDecodeFailures(error.code);

          console.warn("OpaqueId: Decode failed", {
            code: error.code,
            message: error.message,
            publicId: error.publicId,
          });

          // Mapear errores internos a mensajes de UI apropiados
          let userMessage = "Invalid identifier format";

          switch (error.code) {
            case OpaqueIdError.INVALID_VERSION:
              userMessage = "Identifier version not supported";
              break;
            case OpaqueIdError.INVALID_LENGTH:
              userMessage = "Identifier has invalid length";
              break;
            case OpaqueIdError.INVALID_CHAR:
              userMessage = "Identifier contains invalid characters";
              break;
            case OpaqueIdError.CHECKSUM_MISMATCH:
              userMessage = "Identifier appears to be corrupted";
              break;
            case OpaqueIdError.DECODE_FAILED:
              userMessage = "Unable to process identifier";
              break;
          }

          return {
            success: false,
            error: {
              code: error.code,
              message: userMessage,
              publicId: error.publicId,
            },
          };
        }

        incrementDecodeFailures("UNKNOWN_ERROR");
        console.error("OpaqueId: Unexpected decode error", error);

        return {
          success: false,
          error: {
            code: "UNKNOWN_ERROR",
            message: "An unexpected error occurred",
          },
        };
      }
    },
    [
      config,
      incrementDecodeAttempts,
      incrementDecodeSuccesses,
      incrementDecodeFailures,
    ]
  );

  /**
 * Codifica cualquier valor JSON a identificador opaco
 * @param value - Valor JSON-serializable (número, string, objeto, array)
 * @returns Identificador opaco ofuscado
 */
const encodeValue = useCallback(
  <T extends JsonValue>(value: T): OpaqueIdResponse<string> => {
    try {
      incrementEncodeAttempts();

      // 1. Serializar valor a JSON
      const jsonString = JSON.stringify(value);

      // 2. Convertir JSON string a bytes
      const bytes = stringToBytes(jsonString);

      // 3. Encodear bytes usando la nueva función del core
      const result = encodeBytes(bytes, config);

      return {
        success: true,
        data: result,
      };
    } catch (error) {
      if (error instanceof OpaqueIdException) {
        console.warn("OpaqueId: encodeValue failed", {
          code: error.code,
          message: error.message,
          publicId: error.publicId,
        });

        return {
          success: false,
          error: {
            code: error.code,
            message: "Failed to generate identifier",
            publicId: error.publicId,
          },
        };
      }

      console.error("OpaqueId: Unexpected encodeValue error", error);
      return {
        success: false,
        error: {
          code: "UNKNOWN_ERROR",
          message: "An unexpected error occurred",
        },
      };
    }
  },
  [config, incrementEncodeAttempts]
);

/**
 * Decodifica un identificador opaco al valor JSON original
 * @param publicId - Identificador opaco a decodificar
 * @returns Valor original con su tipo preservado
 */
const decodeValue = useCallback(
  <T extends JsonValue>(publicId: string): OpaqueIdResponse<T> => {
    try {
      incrementDecodeAttempts();

      // 1. Decodear a bytes usando la nueva función del core
      const bytes = decodeBytes(publicId, config);

      // 2. Convertir bytes a JSON string
      const jsonString = bytesToString(bytes);

      // 3. Parsear JSON al tipo original
      const value = JSON.parse(jsonString) as T;

      incrementDecodeSuccesses();

      return {
        success: true,
        data: value,
      };
    } catch (error) {
      if (error instanceof OpaqueIdException) {
        incrementDecodeFailures(error.code);

        console.warn("OpaqueId: decodeValue failed", {
          code: error.code,
          message: error.message,
          publicId: error.publicId,
        });

        // Mapear errores
        let userMessage = "Invalid identifier format";

        switch (error.code) {
          case OpaqueIdError.INVALID_VERSION:
            userMessage = "Identifier version not supported";
            break;
          case OpaqueIdError.INVALID_LENGTH:
            userMessage = "Identifier has invalid length";
            break;
          case OpaqueIdError.INVALID_CHAR:
            userMessage = "Identifier contains invalid characters";
            break;
          case OpaqueIdError.CHECKSUM_MISMATCH:
            userMessage = "Identifier appears to be corrupted";
            break;
          case OpaqueIdError.DECODE_FAILED:
            userMessage = "Unable to process identifier";
            break;
        }

        return {
          success: false,
          error: {
            code: error.code,
            message: userMessage,
            publicId: error.publicId,
          },
        };
      }

      // Manejar errores de JSON.parse
      if (error instanceof SyntaxError) {
        incrementDecodeFailures("JSON_PARSE_ERROR");
        console.error("OpaqueId: JSON parse error", error);

        return {
          success: false,
          error: {
            code: "JSON_PARSE_ERROR",
            message: "Failed to parse decoded data",
            publicId,
          },
        };
      }

      incrementDecodeFailures("UNKNOWN_ERROR");
      console.error("OpaqueId: Unexpected decodeValue error", error);

      return {
        success: false,
        error: {
          code: "UNKNOWN_ERROR",
          message: "An unexpected error occurred",
        },
      };
    }
  },
  [
    config,
    incrementDecodeAttempts,
    incrementDecodeSuccesses,
    incrementDecodeFailures,
  ]
);

  const validateLight = useCallback(
    (publicId: string): boolean => {
      return coreValidateLight(publicId, config);
    },
    [config]
  );

  const validate = useCallback(
    (publicId: string): boolean => {
      return coreValidate(publicId, config);
    },
    [config]
  );

  // Helper para construir URLs (SSR-safe)
  const buildUrl = useCallback(
    (num: number, options: BuildUrlOptions): string | null => {
      const encodeResult = encode(num);

      if (!encodeResult.success) {
        return null;
      }

      const { basePath, prefix = "", slug } = options;
      const cleanBasePath = basePath.endsWith("/")
        ? basePath.slice(0, -1)
        : basePath;
      const cleanPrefix = prefix
        ? prefix.startsWith("/")
          ? prefix
          : `/${prefix}`
        : "";

      // Soporte para slugs SEO: /mi-slug--<id>
      if (slug) {
        return `${cleanBasePath}${cleanPrefix}/${slug}--${encodeResult.data}`;
      }

      return `${cleanBasePath}${cleanPrefix}/${encodeResult.data}`;
    },
    [encode]
  );

  // Helper para extraer ID de path (SSR-safe)
  const parseIdFromPath = useCallback(
    (path: string): string | null => {
      if (!path) return null;

      // Extraer el último segmento del path
      const segments = path.split("/").filter(Boolean);
      const lastSegment = segments[segments.length - 1];

      if (!lastSegment) return null;

      // Remover query params y hash si existen
      let cleanSegment = lastSegment.split("?")[0].split("#")[0];

      // Soporte para slugs SEO: extraer ID después de '--'
      if (cleanSegment.includes("--")) {
        const parts = cleanSegment.split("--");
        cleanSegment = parts[parts.length - 1]; // Tomar la última parte después de '--'
      }

      // Validar que parece un ID opaco usando validación ligera
      if (validateLight(cleanSegment)) {
        return cleanSegment;
      }

      return null;
    },
    [validateLight]
  );

  // Helper para decodificar desde URL completa (SSR-safe)
  const decodeFromUrl = useCallback(
    (urlLike: string): OpaqueIdResponse<number> | null => {
      try {
        // Manejar tanto URLs completas como paths
        let path: string;

        if (urlLike.startsWith("http://") || urlLike.startsWith("https://")) {
          const url = new URL(urlLike);
          path = url.pathname;
        } else {
          path = urlLike;
        }

        const publicId = parseIdFromPath(path);

        if (!publicId) {
          return {
            success: false,
            error: {
              code: "NO_ID_FOUND",
              message: "No valid identifier found in URL",
            },
          };
        }

        return decode(publicId);
      } catch (error) {
        console.warn("OpaqueId: Failed to parse URL", { urlLike, error });

        return {
          success: false,
          error: {
            code: "URL_PARSE_ERROR",
            message: "Failed to parse URL",
          },
        };
      }
    },
    [decode, parseIdFromPath]
  );

  // Memoizar el objeto de retorno para evitar re-renders innecesarios
  return useMemo(
    () => ({

      encodeValue,
      decodeValue,
      encode,
      decode,
      validate,
      validateLight,
      buildUrl,
      parseIdFromPath,
      decodeFromUrl,

      // Información de configuración (sin exponer salts ni keyMap)
      config: {
        version: config.version,
        minLength: config.minLength,
        checksumMod: config.checksumMod,
        activeKid: config.activeKid,
      },
    }),
    [
      encodeValue,
      decodeValue,
      encode,
      decode,
      validate,
      validateLight,
      buildUrl,
      parseIdFromPath,
      decodeFromUrl,
      config,
    ]
  );
}

export default useOpaqueId;
